/**
 * MSAL (Microsoft Entra ID) browser configuration + token acquisition.
 *
 * The SPA signs the user in against Entra and acquires an access token scoped
 * to the API (`VITE_ENTRA_API_SCOPE`); every REST call (`./client`) and hub
 * connection (`./signalr`) attaches that token as a bearer credential. The
 * backend validates it with Microsoft.Identity.Web — there is no app-issued JWT.
 *
 * Config comes from build-time Vite env vars. All three are NON-secret public
 * identifiers (they ship in the bundle regardless). Fallbacks keep `npm run
 * build` and tests working when unset; real sign-in only works once the
 * deployment injects real values — see docs/deployment-aws.md.
 *
 * SESSION LIFETIME (ticket #33). Entra caps the refresh token issued to a
 * single-page app at 24 hours and will not renew it. Past that, MSAL's only
 * silent path is a hidden iframe against the Entra session cookie — a
 * third-party cookie for us — which Chrome's storage partitioning now blocks.
 * A day-old tab therefore hits a NORMAL, EXPECTED dead end, and the failure
 * arrives as a `BrowserAuthError` (iframe timed out / empty response), NOT the
 * `InteractionRequiredAuthError` an app would naively catch. Token acquisition
 * here classifies the outcome instead of collapsing it to null, so callers can
 * tell "session is over, ask the user to sign in" apart from "the network
 * blipped, keep the session and retry".
 */
import {
    AuthError,
    BrowserAuthErrorCodes,
    InteractionRequiredAuthError,
    PublicClientApplication,
    type AccountInfo,
    type Configuration,
    type RedirectRequest,
} from '@azure/msal-browser'

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? 'common'
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '00000000-0000-0000-0000-000000000000'
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE ?? `api://${clientId}/access_as_user`

const msalConfig: Configuration = {
    auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: `${window.location.origin}/login`,
    },
    cache: {
        cacheLocation: 'localStorage',
    },
}

/** The API scope the SPA requests so the issued access token carries an
 *  audience the backend can validate. */
export const loginRequest: RedirectRequest = { scopes: [apiScope] }

export const msalInstance = new PublicClientApplication(msalConfig)

/**
 * Outcome of one silent token acquisition. Four distinct cases, because they
 * demand four different responses from the caller:
 *   - `token`                — go make the request.
 *   - `no-account`           — nobody is signed in; show the sign-in screen.
 *   - `interaction-required` — an account is cached but its session is over;
 *                              the stale account must be evicted (`endSession`)
 *                              and the user asked to sign in again.
 *   - `unavailable`          — a transient fault (offline, network error, a
 *                              redirect already mid-flight). The session may
 *                              well be alive: retry, never sign the user out.
 */
export type TokenResult =
    | { kind: 'token'; token: string }
    | { kind: 'no-account' }
    | { kind: 'interaction-required' }
    | { kind: 'unavailable'; error: unknown }

/**
 * MSAL error codes that do NOT mean the session is dead. Network faults are
 * self-evident; `interaction_in_progress` means a redirect we started is still
 * running, and evicting the account underneath it would break that sign-in.
 * Anything outside this set — above all the hidden-iframe failures documented
 * at the top of this file — means silent renewal cannot complete and only an
 * interactive redirect can fix it.
 */
const TRANSIENT_ERROR_CODES: ReadonlySet<string> = new Set<string>([
    BrowserAuthErrorCodes.noNetworkConnectivity,
    BrowserAuthErrorCodes.postRequestFailed,
    BrowserAuthErrorCodes.getRequestFailed,
    BrowserAuthErrorCodes.failedToParseResponse,
    BrowserAuthErrorCodes.interactionInProgress,
])

const classifySilentFailure = (err: unknown): TokenResult => {
    if (err instanceof InteractionRequiredAuthError) return { kind: 'interaction-required' }
    if (!navigator.onLine) return { kind: 'unavailable', error: err }
    if (err instanceof AuthError && TRANSIENT_ERROR_CODES.has(err.errorCode)) {
        return { kind: 'unavailable', error: err }
    }
    return { kind: 'interaction-required' }
}

const cachedAccount = (): AccountInfo | null =>
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null

/**
 * Silently acquire an API access token for the signed-in account. Returns a
 * classified result rather than a nullable string — see `TokenResult`.
 *
 * This deliberately never starts an interactive redirect. It is called from
 * background REST calls and hub token factories, and a navigation fired from
 * there would rip the page out from under whatever the user was doing (an open
 * arm modal, a half-typed adjustment). Interaction is the sign-in button's job.
 *
 * Cheap to call repeatedly: a live access token is served straight from the
 * MSAL cache, and MSAL de-duplicates concurrent identical silent requests.
 */
export const acquireApiToken = async (): Promise<TokenResult> => {
    const account = cachedAccount()
    if (!account) return { kind: 'no-account' }
    try {
        const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account })
        return { kind: 'token', token: result.accessToken }
    } catch (err) {
        return classifySilentFailure(err)
    }
}

type SessionEndedListener = () => void

const sessionEndedListeners = new Set<SessionEndedListener>()

/** Subscribe to session death discovered anywhere — a REST 401, a hub token
 *  factory, the startup probe. Returns the unsubscribe function. */
export const onSessionEnded = (listener: SessionEndedListener): (() => void) => {
    sessionEndedListeners.add(listener)
    return () => {
        sessionEndedListeners.delete(listener)
    }
}

let sessionEnding: Promise<void> | null = null

/**
 * Retire a session that is provably over: evict the dead account from the MSAL
 * cache, then notify subscribers so the UI can route to the sign-in screen.
 *
 * Eviction is the load-bearing half. Leaving the account behind is what made
 * ticket #33 look like a broken login: `getAllAccounts()` stayed non-empty, so
 * every reload believed a session existed and replayed the same doomed silent
 * renewal — tens of seconds of dead air per page load, forever.
 *
 * Local only: `clearCache` touches storage, never the network, and never
 * navigates. Fire-once per page load, so N concurrent failing callers evict
 * once; a fresh sign-in reloads the page and resets this module anyway.
 */
export const endSession = (): Promise<void> => {
    sessionEnding ??= (async () => {
        const account = cachedAccount()
        try {
            await msalInstance.clearCache(account ? { account } : undefined)
        } catch {
            // Best effort: a cache we cannot clear must not block sign-in.
        }
        msalInstance.setActiveAccount(null)
        sessionEndedListeners.forEach((listener) => listener())
    })()
    return sessionEnding
}
